/**
 * Public invitation landing page: `/invite/:token`.
 *
 * Flow:
 * 1. Load the public preview via `useInvitationByToken`. If 404 →
 *    show "invitation not found". If status is revoked/expired/
 *    accepted → show a matter-of-fact "this invitation is no
 *    longer active" message.
 * 2. If the status is pending and the preview loads:
 *    - Not signed in → "Sign in with Google to accept" button.
 *      The button stashes the token in sessionStorage before
 *      redirecting, so the RequireFamily guard can bounce the
 *      user back here after OAuth. No-auth-required preview copy.
 *    - Signed in with matching email → show the accept button.
 *    - Signed in with wrong email → show a "sign out to try a
 *      different account" button.
 * 3. On successful accept, navigate to /dashboard. The auth cache
 *    was already seeded by useAcceptInvitation with the new /me
 *    shape, so RequireFamily sees the populated family and lets
 *    the dashboard render.
 */

import { useEffect, useMemo, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { API_URL, ApiError } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { LoadingShell } from "@/components/LoadingShell";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/features/auth/useAuth";
import { useLogout } from "@/features/auth/useLogout";
import {
  useAcceptInvitation,
  useInvitationByToken,
} from "@/features/invitations/useInvitation";
import { stashPendingInviteToken } from "@/features/invitations/pendingInvite";

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const inspect = useInvitationByToken(token);
  const accept = useAcceptInvitation();
  const logout = useLogout();
  const navigate = useNavigate();

  const preview = inspect.data?.preview ?? null;
  const notFound = inspect.data?.notFound === true;

  // Format expires_at nicely
  const expiresLabel = useMemo(() => {
    if (preview === null) return null;
    const d = new Date(preview.expires_at);
    if (Number.isNaN(d.getTime())) return preview.expires_at;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [preview]);

  // If the user is already in a family AND signed in, they can't
  // accept. Bounce them to the dashboard.
  useEffect(() => {
    if (!authLoading && user !== null) {
      // They'll see the page briefly, but we intentionally don't
      // auto-redirect here — the page shows a helpful "you're
      // already in a family" message below. (We could navigate,
      // but a loud explanation is better than a silent bounce.)
    }
  }, [authLoading, user]);

  if (inspect.isLoading || authLoading) {
    return <LoadingShell message="Loading invitation…" />;
  }

  if (notFound) {
    return (
      <PageFrame eyebrow="Invitation">
        <h1 className="font-serif text-display-md text-ink">
          Invitation <em className="italic font-normal text-coral-deep">not found</em>
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          This link isn't valid. Double-check you copied the whole URL from the
          message you received.
        </p>
      </PageFrame>
    );
  }

  if (preview === null) {
    return (
      <PageFrame eyebrow="Invitation">
        <h1 className="font-serif text-display-md text-ink">
          Couldn't load this invitation.
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          Try refreshing. If it keeps failing, the link may be broken.
        </p>
      </PageFrame>
    );
  }

  // Status checks — only `pending` is actionable.
  if (preview.status !== "pending") {
    return (
      <PageFrame eyebrow="Invitation">
        <h1 className="font-serif text-display-md text-ink">
          This invitation is <em className="italic font-normal text-coral-deep">{preview.status}</em>.
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          {preview.status === "expired" &&
            `The 7-day window ran out on ${expiresLabel}. Ask whoever invited you to send a new link.`}
          {preview.status === "revoked" &&
            "The family owner cancelled this invitation. If that's a surprise, reach out to them directly."}
          {preview.status === "accepted" &&
            "This invitation has already been used. If it wasn't you, contact the family owner."}
        </p>
      </PageFrame>
    );
  }

  // Pending — the actionable path.
  const signedIn = user !== null;
  const emailMatches =
    signedIn && user.email.toLowerCase() === preview.invite_email.toLowerCase();

  const handleSignIn = () => {
    if (typeof token === "string") {
      stashPendingInviteToken(token);
    }
    window.location.href = `${API_URL}/api/auth/google/login`;
  };

  const handleAccept = async () => {
    if (typeof token !== "string") return;
    try {
      await accept.mutateAsync({ token });
      navigate("/dashboard", { replace: true });
    } catch {
      // Error is surfaced via accept.error below.
    }
  };

  return (
    <PageFrame eyebrow="You've been invited">
      <div className="flex flex-col gap-4">
        <h1 className="font-serif text-display-md text-ink">
          Join <em className="italic font-normal text-coral-deep">{preview.family_name}</em>
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          <span className="text-ink-soft font-medium">{preview.invited_by_display_name}</span>{" "}
          invited <span className="font-mono text-[14px] text-ink-soft">{preview.invite_email}</span>{" "}
          to join this family on Make It Count. Invitation expires{" "}
          {expiresLabel}.
        </p>
      </div>

      {!signedIn && (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleSignIn}
            className="self-start inline-flex items-center gap-3 px-6 py-3 rounded-md font-sans font-semibold text-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            Sign in with Google to accept
          </button>
          <p className="font-sans text-[12px] italic text-ink-muted max-w-[560px]">
            You'll need to sign in with the same Google account as{" "}
            <span className="font-mono text-[12px] text-ink-soft">{preview.invite_email}</span> —
            any other account won't be accepted.
          </p>
        </div>
      )}

      {signedIn && emailMatches && (
        <div className="flex flex-col gap-3">
          <div className="font-sans text-[14px] text-ink-soft max-w-[560px]">
            Signed in as{" "}
            <span className="font-mono text-[13px] text-ink">{user.email}</span>. Ready to
            accept.
          </div>
          <button
            type="button"
            onClick={handleAccept}
            disabled={accept.isPending}
            className="self-start inline-flex items-center px-6 py-3 rounded-md font-sans font-semibold text-white shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            {accept.isPending ? "Joining…" : "Accept and join"}
          </button>
          {accept.isError && (
            <div role="alert" className="font-sans text-[13px] text-coral-deep max-w-[560px]">
              {accept.error instanceof ApiError
                ? accept.error.detail
                : "Couldn't accept the invitation. Try again."}
            </div>
          )}
        </div>
      )}

      {signedIn && !emailMatches && (
        <div className="flex flex-col gap-3 max-w-[560px]">
          <div
            role="alert"
            className="rounded-md border border-coral-deep/30 bg-coral-soft/40 px-4 py-3 font-sans text-[13px] text-coral-deep"
          >
            This invitation was sent to{" "}
            <span className="font-mono text-[13px]">{preview.invite_email}</span>, but you're
            signed in as <span className="font-mono text-[13px]">{user.email}</span>. Sign out
            and sign back in with the other account to accept.
          </div>
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="self-start px-5 py-2 rounded-md font-sans text-[14px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          >
            {logout.isPending ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </PageFrame>
  );
}

function PageFrame({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: ReactNode;
}) {
  return (
    <main className="max-w-[640px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col gap-8">
      <div className="flex items-center gap-4">
        <BrandMark />
        <Wordmark size="hero" />
      </div>
      <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
        {eyebrow}
      </div>
      {children}
    </main>
  );
}
